import { Box, Divider } from "@mui/material";
import BookmarkPlan from "components/common/Bookmark";
import Export from "components/common/Export";
import { exportURLs } from "components/common/Export/ExportAPIEndpoints";
import { copyAvailableFiltersToPage } from "components/common/Filters/FiltersFunctions";
import { filterPage } from "components/common/Filters/FiltersHelper";
import GlobalFilterDescription from "components/common/Filters/GlobalFilterDescription";
import GlobalFilters from "components/common/Filters/GlobalFilters";
import KeyMetrics from "components/common/KeyMetrics/KeyMetrics";
import BasicTable from "components/common/Table";
import { uniq } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { abovePropertyViewTypePlan, noAbovePropertyPlans, PAGE_SIZE, tabConstants } from "utils/CommonConstants";
import {
  formFilterRequestPayload,
  functionalAreaIdsToNames,
  getHltInncodes,
  getMembersList,
  getSourceName,
  isAboveUserPlanBookmarked
} from "utils/CommonFunctions";
import { routes } from "utils/routes";
import { serviceTypes } from "utils/Types";
import { fetchAssignedLibraryPlansAsync, setAssignedPlansSnapshot } from "../../AboveProperty.slice";
import { getKeyMetricsPlanViewData } from "../../AbovePropertyHelper";
import { headers } from "./TableHeadersConfig";

const AssignedLibraryPlans = () => {
  const [pageNo, setPageNo] = useState(0);
  const [assignedPlanData, setAssignedPlanData] = useState([]);
  const dispatch: any = useAppDispatch();
  const navigate = useNavigate();
  const assignedLibraryPlan = useAppSelector((state) => state.aboveProperty.assignedLibraryPlan);
  const totalRecords = useAppSelector((state) => state.aboveProperty.totalRecords);
  const bookmarkedIdList = useAppSelector((state) => state.aboveProperty.bookmarkedIdList);
  const assignedPlansSnapshot = useAppSelector((state) => state.aboveProperty.assignedPlansSnapshot);
  const pageName = filterPage.plansPageAssignedPlansDisplay;
  const globalFiltersforPage = useAppSelector((state) => state.globalFilters[pageName]);
  const filterRequestPayload = globalFiltersforPage?.modifiedFilter || globalFiltersforPage?.defaultFilter || [];
  const formattedFilterRequestPayload = formFilterRequestPayload(filterRequestPayload);
  const searchText = globalFiltersforPage?.searchText;

  useEffect(() => {
    if (assignedLibraryPlan?.length) {
      const rowData = assignedLibraryPlan?.map((item: any) => {
        return {
          data: [
            {
              content: (
                <Box
                  onClick={() => {
                    copyAvailableFiltersToPage(dispatch, filterPage.planProgressFromPlansDisplay, filterRequestPayload);
                    navigate(`${routes.PlanProgress}/${item?.cmsId}?redirectFrom=${tabConstants.libraryPlans}`, {
                      state: { progressInncodes: item?.properties }
                    });
                  }}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    color: tokens.Text.General.PrimaryHeadline,
                    fontWeight: "700"
                  }}
                >
                  {item?.name}
                </Box>
              )
            },
            { content: functionalAreaIdsToNames(item?.functionalAreaIds).join(", ") },
            { content: uniq(item?.sources?.map((src: any) => getSourceName(src))).join(", ") },
            { content: item?.propertiesCount },
            {
              content: (
                <BookmarkPlan
                  aboveUserPayload={{
                    isAboveUsertype: true,
                    viewType: abovePropertyViewTypePlan
                  }}
                  cmsId={item?.cmsId}
                  isBookmarked={isAboveUserPlanBookmarked(item?.cmsId, "bookmarkedIdList")}
                />
              )
            }
          ]
        };
      });
      setAssignedPlanData(rowData);
    } else setAssignedPlanData([]);
  }, [assignedLibraryPlan, bookmarkedIdList]);

  useEffect(() => {
    if (pageNo > 0) {
      dispatch(
        fetchAssignedLibraryPlansAsync({
          innCodes: getHltInncodes(),
          searchText: searchText,
          ...formattedFilterRequestPayload,
          pageable: {
            page: pageNo,
            size: PAGE_SIZE
          }
        })
      );
    }
  }, [pageNo]);

  useEffect(() => {
    setPageNo(0);
    dispatch(
      fetchAssignedLibraryPlansAsync({
        innCodes: getHltInncodes(),
        searchText: searchText,
        ...formattedFilterRequestPayload,
        pageable: {
          page: 0,
          size: PAGE_SIZE
        }
      })
    );
  }, [filterRequestPayload, searchText]);

  useEffect(() => {
    dispatch(
      fetchAssignedLibraryPlansAsync({
        innCodes: getHltInncodes(),
        searchText: searchText,
        ...formattedFilterRequestPayload,
        fetchSnapshot: true
      })
    ).then((res: any) => {
      dispatch(setAssignedPlansSnapshot(res?.payload));
    });
  }, [filterRequestPayload]);

  return (
    <Box
      sx={{
        backgroundColor: tokens.Background.General.Primary,
        borderRadius: "12px"
      }}
    >
      <Box sx={{ padding: "16px 8px" }}>
        {" "}
        <GlobalFilters
          isSearchBarVisible
          page={pageName}
          isPreDefinedNav={true}
          searchedText={searchText}
          getMembersList={(searchValue: string) => getMembersList(searchValue, dispatch)}
        />
        <GlobalFilterDescription page={pageName} />
        <Box sx={{ marginTop: "16px" }}>
          <KeyMetrics keyMetricsData={getKeyMetricsPlanViewData(assignedPlansSnapshot)} helperText="*Last 90 Days" />
        </Box>
      </Box>
      {assignedLibraryPlan?.length ? (
        <Box sx={{ display: "flex", marginLeft: "auto", justifyContent: "flex-end", margin: "-8px 9px 16px 0px" }}>
          <Export
            URL={exportURLs.aboveProperty_assignedAndTemplatePlans}
            data={{
              innCodes: getHltInncodes(),
              searchText: searchText,
              ...formattedFilterRequestPayload,
              pageable: {
                page: 0,
                size: 50
              }
            }}
            serviceType={serviceTypes.userplan}
          />
        </Box>
      ) : (
        <></>
      )}
      <Divider />
      <BasicTable
        data={{
          headers,
          rows: assignedPlanData
        }}
        totalRecords={totalRecords}
        noDataMessage={noAbovePropertyPlans}
        pagination={{ pageNo: pageNo, setPageNo: setPageNo }}
      />
    </Box>
  );
};

export default AssignedLibraryPlans;
